// [목적] 신고 사유 선택 모달 — targetType/targetId 를 받아 POST /reports 제출
// [이유] FlowLine A7(054b57db 일부): 폴리오(비소유자)·댓글(비작성자) 신고 버튼 공용 다이얼로그.
//        LoginPromptModal.tsx 와 동일하게 MUI Dialog 로 focus trap/Esc/aria-labelledby 자동 확보.
// [사이드 이팩트] 없음(호출부가 open=true 로 넘길 때만 마운트 의미 있음 — 게스트 가드는 호출부 책임).
//                프로필 신고(다른 FE 에이전트 소관)가 targetType='user' 로 이 컴포넌트를 재사용할 예정.
// [수정 시 고려사항] 에러 코드 매핑(report.TARGET_NOT_FOUND/SELF_REPORT_FORBIDDEN/ALREADY_REPORTED)은
//                   apps/api core/exception/codes.ts 가 정본 — 코드 문자열 변경 시 동기 갱신 필요.
//                   성공 시 1.2초 후 자동 onClose — 별도 성공 콜백 없음(단순 신고 접수 확인 UX).

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { ReportReason, ReportTargetType } from '@gangwoncanvas/types';

import { ApiError } from '../../api/http.js';
import { createReport } from '../../api/report.client.js';

const c = tokens.color;
const L = 'light' as const;

export interface ReportDialogProps {
  targetType: ReportTargetType;
  targetId: string;
  open: boolean;
  onClose: () => void;
}

const REASON_OPTIONS: ReadonlyArray<{ id: ReportReason; label: string }> = [
  { id: 'spam', label: '스팸/광고' },
  { id: 'abuse', label: '욕설/괴롭힘' },
  { id: 'inappropriate', label: '부적절한 콘텐츠' },
  { id: 'other', label: '기타' },
];

/**
 * [목적] ApiError.code → 사용자 안내 문구 매핑
 * [이유] BE 가 404(대상 없음)/400(자기신고)/409(중복신고) 를 dot-namespaced 코드로 구분 반환.
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] 코드 추가 시 이 맵만 갱신하면 됨.
 */
function mapReportError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'report.ALREADY_REPORTED') return '이미 처리 대기 중인 신고가 있어요.';
    if (err.code === 'report.SELF_REPORT_FORBIDDEN') return '자기 자신은 신고할 수 없어요.';
    if (err.code === 'report.TARGET_NOT_FOUND') return '신고 대상을 찾을 수 없어요.';
  }
  return '신고 접수에 실패했어요. 잠시 후 다시 시도해 주세요.';
}

/**
 * [목적] 신고 다이얼로그 본체 — 사유 선택(RadioGroup) + 상세 사유(≤1000자) + 제출
 * [이유] A7: spam|abuse|inappropriate|other 4종 사유 고정(packages/types reportReasonEnum).
 * [사이드 이팩트] 제출 성공 시 Alert(성공) 노출 후 1.2초 뒤 onClose 자동 호출.
 * [수정 시 고려사항] open=false → 컴포넌트 언마운트 대신 Dialog 자체 닫힘(MUI) — 재오픈 시 이전 상태 유지될 수
 *                   있어 handleClose 에서 폼 상태를 초기화한다.
 */
export function ReportDialog({
  targetType,
  targetId,
  open,
  onClose,
}: ReportDialogProps): JSX.Element {
  const [reason, setReason] = useState<ReportReason>('spam');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  function resetAndClose(): void {
    setReason('spam');
    setDetail('');
    setErrorMessage(null);
    setSucceeded(false);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await createReport({
        targetType,
        targetId,
        reason,
        detail: detail.trim() ? detail.trim() : undefined,
      });
      setSucceeded(true);
      setTimeout(resetAndClose, 1200);
    } catch (err) {
      setErrorMessage(mapReportError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      aria-labelledby="report-dialog-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${tokens.radius.l}px`, bgcolor: c.paper[0][L] } }}
    >
      <DialogTitle
        id="report-dialog-title"
        sx={{ fontSize: 16, fontWeight: 700, color: c.ink.high[L] }}
      >
        신고하기
      </DialogTitle>
      <DialogContent>
        {succeeded ? (
          <Alert severity="success" role="status">
            신고가 접수되었습니다. 검토 후 처리됩니다.
          </Alert>
        ) : (
          <>
            {errorMessage && (
              <Alert severity="error" role="alert" sx={{ mb: `${tokens.spacing[2]}px` }}>
                {errorMessage}
              </Alert>
            )}
            <FormControl component="fieldset" sx={{ mb: `${tokens.spacing[2]}px` }}>
              <RadioGroup
                aria-label="신고 사유"
                value={reason}
                onChange={(e) => setReason(e.target.value as ReportReason)}
              >
                {REASON_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.id}
                    value={opt.id}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, 1000))}
              placeholder="상세 사유(선택, 최대 1000자)"
              multiline
              minRows={3}
              fullWidth
              inputProps={{ 'aria-label': '신고 상세 사유', maxLength: 1000 }}
            />
            <Box
              sx={{
                fontSize: 11,
                color: c.ink.low[L],
                textAlign: 'right',
                mt: `${tokens.spacing[1]}px`,
              }}
            >
              {detail.length}/1000
            </Box>
          </>
        )}
      </DialogContent>
      {!succeeded && (
        <DialogActions sx={{ px: `${tokens.spacing[4]}px`, pb: `${tokens.spacing[3]}px` }}>
          <Button onClick={resetAndClose} aria-label="신고 취소" disabled={submitting}>
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            aria-label="신고 제출"
            disabled={submitting}
            sx={{
              bgcolor: c.brand[500][L],
              // [수정: 디자인 개편] MUI 기본 disabled 스타일을 sx.bgcolor 정적값이 덮어써 비활성에도 파란색이 남았다 — .Mui-disabled 로 회색 면/글자를 명시
              '&.Mui-disabled': { bgcolor: c.paper[2][L], color: c.ink.low[L] },
              '&:focus-visible': { outline: `2px solid ${c.brand[500][L]}`, outlineOffset: '2px' },
            }}
          >
            제출
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
